/**
 * Org settings dialog. Reachable from the UserMenu in cloud mode.
 *
 * Tabs:
 *   - Members:    list + remove + change role (owner/admin only)
 *   - Invitations: list + invite + revoke (owner/admin only)
 *   - Billing:    plan, checkout/portal handoff, invoices
 *   - Webhooks:   destination allow-list for webhook deploys (owner-edited)
 *   - Audit log:  read-only, latest 50 events
 *
 * The role-gated mutations don't pre-check on the client — the server
 * 403s if the current user can't perform them, and we surface that
 * inline rather than hiding buttons (less confusing when admin changes
 * happen out-of-band).
 */

import { useEffect, useRef, useState } from 'react';

import {
  type AuditEvent,
  type BillingStatus,
  type InvitationRow,
  type MemberRow,
  orgApi,
  type OrgApi,
  type OrgRole,
} from '../data/orgApi.js';

import { Spinner } from './Spinner.js';
import './OrgSettings.css';

interface Props {
  orgSlug: string;
  open: boolean;
  onClose: () => void;
}

type Tab = 'members' | 'invitations' | 'billing' | 'webhooks' | 'audit';

export function OrgSettings({ orgSlug, open, onClose }: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [tab, setTab] = useState<Tab>('members');

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onClose();
    d.addEventListener('close', handler);
    return () => d.removeEventListener('close', handler);
  }, [onClose]);

  const api = orgApi({ orgSlug });

  return (
    <dialog ref={dialogRef} className="org-settings-dialog" aria-labelledby="os-title">
      <header className="org-settings-header">
        <h2 id="os-title" className="org-settings-title">
          Organization settings · {orgSlug}
        </h2>
        <button type="button" className="org-settings-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <div className="org-settings-tabs" role="tablist">
        <TabButton current={tab} value="members" onSelect={setTab}>
          Members
        </TabButton>
        <TabButton current={tab} value="invitations" onSelect={setTab}>
          Invitations
        </TabButton>
        <TabButton current={tab} value="billing" onSelect={setTab}>
          Billing
        </TabButton>
        <TabButton current={tab} value="webhooks" onSelect={setTab}>
          Webhooks
        </TabButton>
        <TabButton current={tab} value="audit" onSelect={setTab}>
          Audit log
        </TabButton>
      </div>

      <div className="org-settings-body">
        {tab === 'members' && <MembersPanel api={api} />}
        {tab === 'invitations' && <InvitationsPanel api={api} />}
        {tab === 'billing' && <BillingPanel api={api} />}
        {tab === 'webhooks' && <WebhookHostsPanel api={api} />}
        {tab === 'audit' && <AuditPanel api={api} />}
      </div>
    </dialog>
  );
}

function TabButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: Tab;
  value: Tab;
  onSelect: (t: Tab) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={'org-settings-tab' + (active ? ' is-active' : '')}
      onClick={() => onSelect(value)}
    >
      {children}
    </button>
  );
}

/* ---------------- Members ---------------- */

function MembersPanel({ api }: { api: OrgApi }): JSX.Element {
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      setMembers(await api.listMembers());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoleChange = async (userId: string, role: OrgRole): Promise<void> => {
    try {
      await api.updateMemberRole(userId, role);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role.');
    }
  };

  const handleRemove = async (userId: string, email: string): Promise<void> => {
    if (!window.confirm(`Remove ${email} from the organization?`)) return;
    try {
      await api.removeMember(userId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member.');
    }
  };

  if (members === null && !error) return <Spinner label="Loading…" />;

  return (
    <div className="org-settings-panel">
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}
      <table className="org-settings-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Role</th>
            <th>Joined</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {(members ?? []).map((m) => (
            <tr key={m.userId}>
              <td>
                <div className="org-settings-member-name">{m.name ?? m.email}</div>
                {m.name && <div className="org-settings-member-email">{m.email}</div>}
              </td>
              <td>
                <select
                  className="input"
                  value={m.role}
                  onChange={(e) => void handleRoleChange(m.userId, e.target.value as OrgRole)}
                >
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                </select>
              </td>
              <td>{new Date(m.joinedAt).toLocaleDateString()}</td>
              <td>
                <button
                  type="button"
                  className="org-settings-remove"
                  onClick={() => void handleRemove(m.userId, m.email)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Invitations ---------------- */

function InvitationsPanel({ api }: { api: OrgApi }): JSX.Element {
  const [invites, setInvites] = useState<InvitationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('member');
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    try {
      setInvites(await api.listInvitations());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invitations.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInvite = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember({ email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: string): Promise<void> => {
    try {
      await api.revokeInvitation(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke.');
    }
  };

  return (
    <div className="org-settings-panel">
      <form className="org-settings-invite-form" onSubmit={(e) => void handleInvite(e)}>
        <input
          type="email"
          required
          className="input"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="teammate@example.com"
          aria-label="Invitee email"
        />
        <select
          className="input"
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as OrgRole)}
          aria-label="Role"
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
        </select>
        <button type="submit" className="landing-button-primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </form>

      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}

      {invites === null ? (
        <Spinner label="Loading…" />
      ) : invites.length === 0 ? (
        <p className="org-settings-empty">No pending invitations.</p>
      ) : (
        <table className="org-settings-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Sent</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {invites.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>{inv.orgRole}</td>
                <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                <td>
                  {inv.acceptedAt
                    ? 'Accepted'
                    : new Date(inv.expiresAt) < new Date()
                      ? 'Expired'
                      : 'Pending'}
                </td>
                <td>
                  {!inv.acceptedAt && (
                    <button
                      type="button"
                      className="org-settings-remove"
                      onClick={() => void handleRevoke(inv.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ---------------- Billing ---------------- */

function BillingPanel({ api }: { api: OrgApi }): JSX.Element {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    try {
      const b = await api.getBilling();
      setBilling(b);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing.');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both flows hand off to a Stripe-hosted page, so we leave the SPA.
  const handleCheckout = async (plan: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startCheckout({ plan });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout.');
      setBusy(false);
    }
  };

  const handlePortal = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.openBillingPortal();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal.');
      setBusy(false);
    }
  };

  if (billing === null && !error) return <Spinner label="Loading…" />;

  return (
    <div className="org-settings-panel">
      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}

      {billing && (
        <>
          <div className="org-settings-billing-current">
            <div className="org-settings-billing-current-head">
              <div>
                <div className="org-settings-billing-plan">{billing.planDisplay.name}</div>
                <div className="org-settings-billing-price">{billing.planDisplay.priceLabel}</div>
              </div>
              <div className="org-settings-billing-status">
                <span className={'org-settings-badge is-' + statusTone(billing.status)}>
                  {billing.status}
                </span>
                {billing.subscription?.currentPeriodEnd && (
                  <span className="org-settings-billing-renews">
                    {billing.subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'}{' '}
                    {new Date(billing.subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            {billing.planDisplay.features.length > 0 && (
              <ul className="org-settings-billing-features">
                {billing.planDisplay.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
            {billing.portalAvailable && (
              <button
                type="button"
                className="landing-button-primary"
                onClick={() => void handlePortal()}
                disabled={busy}
              >
                Manage billing
              </button>
            )}
          </div>

          {billing.availablePlans.length > 0 && (
            <div className="org-settings-billing-plans">
              <h3 className="org-settings-billing-subtitle">
                {billing.plan === 'free' ? 'Upgrade your plan' : 'Change plan'}
              </h3>
              {!billing.configured && (
                <p className="org-settings-empty">
                  Billing isn’t enabled on this deployment yet — these plans are a preview.
                </p>
              )}
              <div className="org-settings-plan-grid">
                {billing.availablePlans.map((p) => {
                  const isCurrent = p.slug === billing.plan;
                  return (
                    <div
                      key={p.slug}
                      className={'org-settings-plan-card' + (isCurrent ? ' is-current' : '')}
                    >
                      <div className="org-settings-plan-name">{p.name}</div>
                      <div className="org-settings-plan-price">{p.priceLabel}</div>
                      <p className="org-settings-plan-desc">{p.description}</p>
                      <ul className="org-settings-billing-features">
                        {p.features.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <span className="org-settings-plan-current-tag">Current plan</span>
                      ) : (
                        <button
                          type="button"
                          className="landing-button-primary"
                          disabled={busy || !billing.configured || !p.purchasable}
                          onClick={() => void handleCheckout(p.slug)}
                        >
                          {billing.subscription ? `Switch to ${p.name}` : 'Subscribe'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="org-settings-billing-invoices">
            <h3 className="org-settings-billing-subtitle">Invoices</h3>
            {billing.invoices.length === 0 ? (
              <p className="org-settings-empty">No invoices yet.</p>
            ) : (
              <table className="org-settings-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>Amount</th>
                    <th aria-label="Link" />
                  </tr>
                </thead>
                <tbody>
                  {billing.invoices.map((inv, i) => (
                    <tr key={inv.number ?? String(i)}>
                      <td>{new Date(inv.at).toLocaleDateString()}</td>
                      <td>{inv.number ?? '—'}</td>
                      <td>{formatAmount(inv.total, inv.currency)}</td>
                      <td>
                        {inv.url && (
                          <a href={inv.url} target="_blank" rel="noreferrer">
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function statusTone(status: string): 'ok' | 'warn' | 'bad' {
  if (status === 'active' || status === 'trialing') return 'ok';
  if (status === 'past_due' || status === 'incomplete') return 'warn';
  return 'bad';
}

function formatAmount(minorUnits: number, currency: string): string {
  return `${(minorUnits / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

/* ---------------- Webhook destination allow-list ---------------- */

function WebhookHostsPanel({ api }: { api: OrgApi }): JSX.Element {
  const [hosts, setHosts] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newHost, setNewHost] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .getWebhookAllowedHosts()
      .then((h) => {
        if (!cancelled) setHosts(h);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Replace the whole list server-side; returns true on success. */
  const save = async (next: string[]): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      setHosts(await api.setWebhookAllowedHosts(next));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const host = newHost.trim();
    if (!host) return;
    if (await save([...(hosts ?? []), host])) setNewHost('');
  };

  const handleRemove = async (host: string): Promise<void> => {
    await save((hosts ?? []).filter((h) => h !== host));
  };

  if (hosts === null && !error) return <Spinner label="Loading…" />;

  return (
    <div className="org-settings-panel">
      <p className="org-settings-intro">
        Webhook deploys can only deliver to hosts on this list. Enter plain hostnames (e.g.{' '}
        <code>hooks.example.com</code>) — no scheme, port, or path. Only the org owner can change
        the list; removing a host immediately disables deploy targets that point at it.
      </p>

      <form className="org-settings-invite-form" onSubmit={(e) => void handleAdd(e)}>
        <input
          type="text"
          required
          className="input"
          value={newHost}
          onChange={(e) => setNewHost(e.target.value)}
          placeholder="hooks.example.com"
          aria-label="Hostname to allow"
        />
        <button type="submit" className="landing-button-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Allow host'}
        </button>
      </form>

      {error && (
        <p className="org-settings-error" role="alert">
          {error}
        </p>
      )}

      {(hosts ?? []).length === 0 ? (
        <p className="org-settings-empty">
          No hosts allowed yet — webhook deploy targets can’t be created or delivered until an owner
          adds one.
        </p>
      ) : (
        <table className="org-settings-table">
          <thead>
            <tr>
              <th>Host</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {(hosts ?? []).map((h) => (
              <tr key={h}>
                <td>
                  <code>{h}</code>
                </td>
                <td>
                  <button
                    type="button"
                    className="org-settings-remove"
                    disabled={busy}
                    onClick={() => void handleRemove(h)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ---------------- Audit log ---------------- */

function AuditPanel({ api }: { api: OrgApi }): JSX.Element {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listAuditEvents({ limit: 50 })
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error)
    return (
      <p className="org-settings-error" role="alert">
        {error}
      </p>
    );
  if (events === null) return <Spinner label="Loading…" />;
  if (events.length === 0) return <p className="org-settings-empty">No audit events yet.</p>;

  return (
    <ul className="org-settings-audit">
      {events.map((e) => (
        <li key={e.id} className="org-settings-audit-row">
          <div className="org-settings-audit-when">{new Date(e.at).toLocaleString()}</div>
          <div className="org-settings-audit-action">
            <code>{e.action}</code>
            {e.actor && <span className="org-settings-audit-actor"> · {e.actor.email}</span>}
          </div>
          {e.entityId && (
            <div className="org-settings-audit-entity">
              {e.entityType}: <code>{e.entityId}</code>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
